// src/components/UploadContacts.jsx
import React, { useState, useEffect } from 'react';
import { useQueryClient } from "@tanstack/react-query";
import api from '../api';

export default function UploadContacts() {
  const queryClient = useQueryClient();

  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await api.get('contacts/show-groups');
        setGroups(res.data?.data || []);
      } catch (error) {
        console.error('Failed to fetch groups:', error);
        setMsg('Failed to load groups');
      }
    };
    fetchGroups();
  }, []);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const upload = async () => {
    if (!file) {
      setMsg('Please select a file first');
      return;
    }

    setLoading(true);
    setMsg('');

    try {
      const fd = new FormData();
      fd.append('file', file);
      if (selectedGroup) {
        fd.append('group', selectedGroup);
      }
      const res = await api.post('/upload/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMsg(`Imported ${res.data.imported} contacts successfully!`);
      setFile(null);
      queryClient.invalidateQueries(["contacts"]);
    } catch (error) {
      console.error('Upload error:', error);
      setMsg(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="yc-upload-section">
      {/* Drop zone */}
      <div
        className={`yc-upload-dropzone ${dragActive ? 'yc-upload-dropzone--active' : ''} ${file ? 'yc-upload-dropzone--has-file' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById('yc-file-input').click()}
      >
        <input
          id="yc-file-input"
          type="file"
          accept=".csv,.xls,.xlsx"
          onChange={e => setFile(e.target.files[0])}
          disabled={loading}
          style={{ display: 'none' }}
        />
        {file ? (
          <div className="yc-upload-file-info">
            <span className="yc-upload-file-icon">📄</span>
            <div>
              <div className="yc-upload-file-name">{file.name}</div>
              <div className="yc-upload-file-size">
                {(file.size / 1024).toFixed(1)} KB
              </div>
            </div>
            <button
              className="yc-upload-remove-btn"
              onClick={(e) => { e.stopPropagation(); setFile(null); }}
              title="Remove file"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="yc-upload-placeholder">
            <span className="yc-upload-icon">📁</span>
            <span className="yc-upload-text">
              Drag & drop a file here, or <strong>click to browse</strong>
            </span>
            <span className="yc-upload-hint">Supports CSV, XLS, XLSX</span>
          </div>
        )}
      </div>

      {/* Controls row */}
      <div className="yc-upload-controls">
        <div className="yc-upload-select-wrap">
          <select
            className="yc-upload-select"
            value={selectedGroup}
            onChange={e => setSelectedGroup(e.target.value)}
            disabled={loading}
          >
            <option value="">Assign to group (optional)</option>
            {groups.map(group => (
              <option key={group._id} value={group._id}>{group.name}</option>
            ))}
          </select>
          <svg className="yc-upload-select-arrow" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </div>
        <button
          className="yc-upload-btn"
          onClick={upload}
          disabled={loading || !file}
        >
          {loading ? (
            <>
              <span className="yc-upload-spinner"></span>
              Uploading...
            </>
          ) : (
            <>⬆ Upload</>
          )}
        </button>
      </div>

      {/* Message */}
      {msg && (
        <div className={`yc-upload-msg ${msg.includes('Error') ? 'yc-upload-msg--error' : 'yc-upload-msg--success'}`}>
          {msg.includes('Error') ? '⚠ ' : '✓ '}{msg}
        </div>
      )}
    </div>
  );
}
